import React, { useCallback, useEffect, useState } from 'react';
import { PowerBiReport } from 'views/ReportPage/components/PowerBiReport';
import { useDispatch } from 'react-redux';
import { TOAST_FAIL, Toaster } from 'components/Toaster/Toaster';
import { ReportToken } from 'core/slices/models';
import { toastOpen } from 'core/slices/toasts';
import { AppDispatch } from 'core/store';
import { emptyToken, INTERVAL_TIME } from './settings';
import { tokenService } from './tokenService';

type PbiReportViewerProps = {
  reportId: string;
  activeProjectKey: string;
};

export const PbiReportViewer: React.FC<PbiReportViewerProps> = ({
  reportId,
  activeProjectKey,
}) => {
  const [token, setToken] = useState<ReportToken>(emptyToken);
  const dispatch = useDispatch<AppDispatch>();

  const checkTokenAndUpdate = useCallback(() => {
    const updateToken = async (projectKey: string, reportId: string) => {
      // Generate a new embed token or refresh the user Azure AD access token
      tokenService
        .getTokenFor(activeProjectKey, reportId)
        .then(reportToken => {
          setToken(reportToken);
        })
        .catch(errResponse => {
          dispatch(
            toastOpen({
              className: TOAST_FAIL,
              text: 'Failed to refresh token. Please try again later.',
            })
          );
        });
    };

    // Update the token if it is about to expire
    if (tokenService.isAboutToExpire(token)) {
      updateToken(activeProjectKey, reportId).then(_ => {});
    }
  }, [activeProjectKey, reportId, token, dispatch]);

  // fetch token if empty and then refresh it in the back
  useEffect(() => {
    setToken(emptyToken);
    tokenService
      .getTokenFor(activeProjectKey, reportId)
      .then(reportToken => {
        setToken(reportToken);
      })
      .catch(errResponse => {
        dispatch(
          toastOpen({
            className: TOAST_FAIL,
            text: 'Failed to load report. Please try again later.',
          })
        );
      });
  }, [reportId, activeProjectKey, dispatch]);

  // refresh the token when it's about to expire
  // Add a listener to make sure token is updated after tab was inactive
  useEffect(() => {
    const interval = setInterval(checkTokenAndUpdate, INTERVAL_TIME);

    const eventHandler = () => {
      // Check the access token when the tab is visible
      if (!document.hidden) {
        checkTokenAndUpdate();
      }
    };
    document.addEventListener('visibilitychange', eventHandler);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', eventHandler);
    };
  }, [checkTokenAndUpdate, reportId]);

  return (
    <>
      <PowerBiReport
        tokenService={tokenService}
        reportId={reportId}
        reportToken={token.token}
      />
      <Toaster />
    </>
  );
};
